import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Row } from 'reactstrap';
import isEmpty from 'lodash/isEmpty';

import { fetchRecentlyViewed, getPersistedRecentlyViewedSlugs } from 'Redux/ducks/recently-viewed';
import BrowseButton from 'Components/Utility/BrowseButton';
import RecentlyViewedContainer from 'Containers/RecentlyViewed/RecentlyViewedContainer';
import ProductCarousel from 'Components/HomePage/ProductCarousel';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import SkeletonCarousel from 'Components/Utility/Skeleton/SkeletonCarousel';

const RecentlyViewedSection = ({
  recentlyViewedHits,
  updateRecentlyViewed,
  headingTag,
  intl,
  hitCardNameTag,
  allSlugs,
  excludeCartItems,
}) => {
  useEffect(() => {
    updateRecentlyViewed(12, excludeCartItems ? allSlugs : []);
  }, [updateRecentlyViewed, allSlugs, excludeCartItems]);

  const Tag = headingTag;

  const vistedProducts = getPersistedRecentlyViewedSlugs().filter(slug => !allSlugs.includes(slug));

  if (recentlyViewedHits.length < 4) return false;

  const { formatMessage } = intl;
  const messages = defineMessages({
    lastCardTitle: {
      id: 'recentlyviewed.last-card-title',
      defaultMessage: 'Recently Viewed',
    },
  });

  const mdScreenWidth = 768;

  let numberOfCards = 3;

  if (window.innerWidth > mdScreenWidth) {
    numberOfCards = 4;
  }

  return (
    <section className="container RecentlyViewedSection">
      <div className="section-heading-container">
        <Tag>
          <FormattedMessage id="recentlyviewed.title" defaultMessage="Recently Viewed" />
        </Tag>
        <BrowseButton className={'view-all-btn'} location={'recently-viewed'} />
      </div>

      {isEmpty(recentlyViewedHits) && vistedProducts.length > 4 &&
        <div className="pb-5">
          <SkeletonCarousel slidesToShow={4} />
        </div>
      }

      {isEmpty(recentlyViewedHits) && vistedProducts.length < 5 &&
        <Row>
          <SkeletonHitCard
            numberOfCards={numberOfCards}
            lgSize={3}
            mdSize={4}
            smSize={6}
            xsSize={6}
          />
        </Row>
      }

      {!isEmpty(recentlyViewedHits) && recentlyViewedHits.length < 5 &&
        <Row>
          <RecentlyViewedContainer fetchHits={false} hitsToDisplay={4} />
        </Row>
      }

      {!isEmpty(recentlyViewedHits) && recentlyViewedHits.length >= 5 &&
        <ProductCarousel
          hitData={recentlyViewedHits}
          slidesToShow={4}
          viewAllLocation={'recently-viewed'}
          viewAllTitle={formatMessage(messages.lastCardTitle)}
          hitCardNameTag={hitCardNameTag}
        />
      }
    </section>
  );
};

RecentlyViewedSection.propTypes = {
  recentlyViewedHits: PropTypes.arrayOf(PropTypes.shape({})),
  updateRecentlyViewed: PropTypes.func.isRequired,
  headingTag: PropTypes.string,
  hitCardNameTag: PropTypes.string,
  excludeCartItems: PropTypes.bool,
  allSlugs: PropTypes.arrayOf(PropTypes.string),
};

RecentlyViewedSection.defaultProps = {
  recentlyViewedHits: [],
  headingTag: 'h2',
  hitCardNameTag: 'div',
  allSlugs: [],
  excludeCartItems: false,
};

const mapStateToProps = ({
  recentlyViewed: { recentlyViewedHits },
  cart: { allSlugs },
}) => ({
  recentlyViewedHits,
  allSlugs,
});

export default connect(mapStateToProps, {
  updateRecentlyViewed: fetchRecentlyViewed,
})(injectIntl(RecentlyViewedSection));
